import React, { FC } from 'react';
import { SkeletonBase } from './skeleton-base';
import type { GDSSkeletonBaseProps } from './skeleton-base';
import { styled } from '../../theme';
import type { GDSResponsiveProp } from '../../types';
import { useResponsiveProp } from '../../hooks/use-responsive-prop';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

export interface GDSSkeletonTextProps
  extends Omit<GDSSkeletonBaseProps, 'height'> {
  lines?: number;
  header?: boolean;
  alignment?: GDSResponsiveProp<
    | 'start'
    | 'end'

    /**
     * @deprecated  use `start` instead
     */
    | 'left'

    /**
     * @deprecated  use `end` instead
     */
    | 'right'
    | 'center'
  >;
}

const DISPLAY_NAME = 'SkeletonText';
const LINE_WIDTHS = [100, 93, 81, 55, 66, 99, 88];
const LAST_LINE = 35;

type GDSGenerateLines = Omit<GDSSkeletonTextProps, 'alignment'>;

const generateLines = ({
  lines = 1,
  header,
  width,
  borderRadius,
  css,
  'data-testid': testId,
}: GDSGenerateLines) => {
  const result = [];
  for (let index = 0; index < lines; index += 1) {
    const lineWidth =
      width ||
      (lines > 1 && index === lines - 1
        ? LAST_LINE
        : LINE_WIDTHS[index % LINE_WIDTHS.length]);

    result.push(
      <SkeletonBase
        css={css}
        key={index}
        height={header ? '$two' : '$one'}
        width={width || `${lineWidth}%`}
        borderRadius={borderRadius}
        data-testid={testId}
      />,
    );
  }
  return result;
};

const SkeletonTextWrapper = styled('div', {
  textAlign: '$$alignment',
  display: 'flex',
  gap: '$half',
  width: '100%',
  flexDirection: 'column',
  variants: {
    alignment: {
      left: { alignItems: 'start' },
      start: { alignItems: 'start' },
      right: { alignItems: 'end' },
      end: { alignItems: 'end' },
      center: { alignItems: 'center' },
    },
  },
});

export const SkeletonText: FC<GDSSkeletonTextProps> = ({
  lines = 1,
  header = false,
  alignment = 'start',
  borderRadius,
  width,
  css,
  'data-testid': testId = `GDS-${DISPLAY_NAME}`,
  ...props
}) => (
  <GenesisCoreInspector displayName={DISPLAY_NAME}>
    <SkeletonTextWrapper
      alignment={useResponsiveProp(alignment)}
      data-testid={testId}
      {...props}
    >
      {generateLines({
        lines,
        header,
        width: useResponsiveProp(width),
        borderRadius,
        css,
        'data-testid': testId,
      })}
    </SkeletonTextWrapper>
  </GenesisCoreInspector>
);

SkeletonText.displayName = DISPLAY_NAME;
